export const filterColorOptions = [
  { id: 'blue', color: '#3785D3' },
  { id: 'pink', color: '#EF476F' },
  { id: 'teal', color: '#07C593' },
  { id: 'purple', color: '#7B66FF' },
  { id: 'magenta', color: '#D766FF' },
  { id: 'red', color: '#FB2C36' },
  { id: 'green', color: '#00C951' },
  { id: 'yellow', color: '#F0B100' },
] as const;

export type FilterColorId = (typeof filterColorOptions)[number]['id'];

/** Default step colors, cycled by index when a step has no stored color. */
export const filterColors: string[] = filterColorOptions
  .slice(0, 5)
  .map((option) => option.color);

export const unmatchedColor = '#cccccc';

const hexToRgb = (color: string): [number, number, number] | undefined => {
  const match = /^#?([0-9A-F]{6})$/i.exec(color);
  if (!match) {
    return undefined;
  }
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

const channelLuminance = (channel: number): number => {
  const srgb = channel / 255;
  return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = (color: string): number => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return 0;
  }
  const [r, g, b] = rgb.map(channelLuminance);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const getFilterColor = (
  index: number,
  color?: string | null,
): string => {
  //The index is -1 when a table row or marker represent an unmatched entity.
  if (index === -1) {
    return unmatchedColor;
  }
  if (color) {
    return color;
  }
  return filterColors[index % filterColors.length];
};

/** Text color that stays readable on a filter step background. */
export const getFilterForegroundColor = (background: string): string =>
  relativeLuminance(background) > 0.4 ? '#121626' : '#FFFFFF';

// Marker pin geometry: an outer "border" layer (circle + rotated square forming
// the pointed tail) and an inner layer. Both are tinted with the filter color,
// the border in a darker shade so it reads as an outline. Rendering it as an
// inline data URI lets every filter color reuse the same shape instead of
// shipping a separate PNG per color.
const PIN_VIEWBOX = '0 0 74.427582 89.646294';
const PIN_ASPECT = 74.427582 / 89.646294;
const PIN_HEIGHT = 20;
const PIN_WIDTH = Math.round(PIN_HEIGHT * PIN_ASPECT);
const PIN_TRANSFORM = 'matrix(0.70879672,0.7054128,-0.70879672,0.7054128,0,0)';

/** Darkens a hex color towards black by `amount` (0–1) so the pin border reads
 *  as an outline of the same hue rather than a flat gray. */
const darkenHex = (color: string, amount: number): string => {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return color;
  }
  const factor = 1 - amount;
  const channel = (value: number): string =>
    Math.round(value * factor)
      .toString(16)
      .padStart(2, '0');
  return `#${rgb.map(channel).join('')}`;
};

const coloredPinDataUri = (color: string): string => {
  const borderColor = darkenHex(color, 0.35);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="${PIN_VIEWBOX}">` +
    `<g transform="translate(-79.372082,-80.152781)">` +
    `<circle cx="116.58588" cy="117.36658" r="37.213795" fill="${borderColor}"/>` +
    `<rect width="38.451942" height="38.451942" x="164.09868" y="-0.29376626" transform="${PIN_TRANSFORM}" fill="${borderColor}"/>` +
    `<circle cx="116.8603" cy="117.23412" r="30.949987" fill="${color}"/>` +
    `<rect width="31.979731" height="31.979731" x="164.42282" y="-0.37224975" transform="${PIN_TRANSFORM}" fill="${color}"/>` +
    `</g></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Warning-triangle geometry: a rounded triangle with an exclamation mark (the
// same base shape as `public/map_icons/inactive.svg`). Rendered inline so it can
// be tinted per filter color, this is used for stops that match a filter but are
// inactive: the same filter tint as the pin (fill in the step color, outline +
// exclamation mark in a darker shade) so an inactive match reads as "this belongs
// to the filter, but needs attention" while sharing the tinting scheme with the
// active pin.
const TRIANGLE_SIZE = 22;

const coloredTriangleWarningDataUri = (color: string): string => {
  const borderColor = darkenHex(color, 0.35);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${TRIANGLE_SIZE}" height="${TRIANGLE_SIZE}" viewBox="0 0 24 24">` +
    `<polygon points="12,3.5 20.5,19.5 3.5,19.5" fill="${color}" stroke="${borderColor}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<line x1="12" y1="9.5" x2="12" y2="14" stroke="${borderColor}" stroke-width="2" stroke-linecap="round"/>` +
    `<circle cx="12" cy="16.5" r="1.1" fill="${borderColor}"/>` +
    `</svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// There are only a handful of distinct icons (one per tint, times the
// active/inactive shape), but `getFilterMarkerIconUrl` is called once per entity
// on every filter run — potentially thousands of times. Cache the built data
// URIs so the string building, hex darkening and `encodeURIComponent` run once
// per unique `${tint}|${inactive}` combination instead of once per marker.
const iconUrlCache = new Map<string, string>();

/** Map-marker icon for a filter step: the shared pin shape tinted with the
 *  step's color (or the unmatched gray for index -1). Inactive stops that still
 *  match the filter use the tinted warning triangle instead of the pin. */
export const getFilterMarkerIconUrl = (
  index: number,
  color?: string | null,
  inactive = false,
): string => {
  const tint = getFilterColor(index, color);
  const cacheKey = `${tint}|${inactive ? 1 : 0}`;
  const cached = iconUrlCache.get(cacheKey);
  if (cached !== undefined) {
    return cached;
  }
  const url = inactive
    ? coloredTriangleWarningDataUri(tint)
    : coloredPinDataUri(tint);
  iconUrlCache.set(cacheKey, url);
  return url;
};
